import { Text } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { usePayment } from "@/hooks/usePayment";
import PaginationControls from "@/pages/pagamentos/pagination-controles";
import { formatCurrency, formatDate } from "@/utils";
import { DynamicTable } from "@/components";
import { DrawerDetailsPaymentBatch } from "../drawer-detalhes-pagamento";
import { Button, Tooltip } from "barte-design-system";

export function PaymentsBatchTable() {
    const user = JSON.parse(localStorage.getItem("barte-user-info"));
    const { 
      getListPaymentsBatchesInitialData, 
      getPaymentBatchDetailInitialData,
      paymentBatches: paymentBatchesFromHook, 
      isPaymentBatchesLoading: isPaymentBatchesLoadingFromHook,
      batchesPaginationInfo: batchesPaginationInfoFromHook,
      isNewPaymentDrawerOpen,
      isDetailsPaymentBatchDrawerOpen,
      onDetailsPaymentBatchDrawerClose,
      onDetailsPaymentBatchDrawerOpen,
    } = usePayment();

    const paymentBatches =  paymentBatchesFromHook;
    const batchesPaginationInfo =   batchesPaginationInfoFromHook;
    
    //Variables
    const sortDirection = 'DESC';
    const sortField = 'createdAt';
      
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedBatches, setExpandedBatches] = useState<number[]>([]);
    const [loadingBatches, setLoadingBatches] = useState<number[]>([]);
    const [pageSize, setPageSize] = useState(10);

    //Columns
    const columns: any[] = [
        {
            title: "Data de envio",
            dataIndex: "batchUploadDate",
            key: "batchUploadDate",
            sorter: false,
            render: (batchUploadDate: string) => (
                <>
                    <Text>{formatDate(batchUploadDate.split('T')[0])}</Text>
                </>
            ),
        },
        {
            key: "name",
            sorter: false,
            title: "Nome do Lote",
            dataIndex: "name",
            render: (name: string) => (
                <Tooltip label={name} placement="top">
                    <Text maxW="250px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{name}</Text>
                </Tooltip>
            ),
        },
        {
            key: "filename",
            sorter: false,
            title: "Nome do arquivo",
            dataIndex: "filename",
            render: (filename: string) => (
                <Tooltip label={filename} placement="top">
                    <Text maxW="250px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{filename}</Text>
                </Tooltip>
            ),
        },
        {
            title: "Valor total",
            key: "paymentsTotalAmount",
            sorter: false,
            dataIndex: "paymentsTotalAmount",
            render: (paymentsTotalAmount: number) => (
                <Text>{formatCurrency({ value: Number(paymentsTotalAmount) })}</Text>
            ),
        },
        {
            key: "buttonOptions",
            title: "",
            dataIndex: "",
            render: (value, record: any) => (
              <Tooltip label="Visualizar detalhes">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBatchExpand(record.id)}
                  disabled={loadingBatches.includes(record.id)}
                >
                  Detalhes
                </Button>
              </Tooltip>
            ),
        },
    ]
    //Callbacks // Event handlers
    const handlePageChange = useCallback((newPage) => {
        setCurrentPage(newPage);
    }, []);

    const handleBatchExpand = useCallback(async (batchId: number) => {
        const isExpanded = expandedBatches.includes(batchId);
        if (!isExpanded) {
            // Expandir: buscar detalhes
            setLoadingBatches(prev => [...prev, batchId]);
            try {
                await getPaymentBatchDetailInitialData(batchId);
                setExpandedBatches(prev => [...prev, batchId]);
            } catch (error) {
                console.error('Erro ao buscar detalhes do batch:', error);
            } finally {
                setLoadingBatches(prev => prev.filter(id => id !== batchId));
            }
        } else {
            // Colapsar: apenas remover da lista
            setExpandedBatches(prev => prev.filter(id => id !== batchId));
        }
        onDetailsPaymentBatchDrawerOpen();
    }, [expandedBatches, getPaymentBatchDetailInitialData]);
  
    // Filtrar
    const fetchPaymentBatches = useCallback(async () => {
        const pageable = {
            page: currentPage - 1, // Backend usa 0-based pagination
            size: pageSize,
            sort: `${sortField},${sortDirection}`,
        };

        await getListPaymentsBatchesInitialData(pageable);
    }, [
        currentPage,
        sortField,
        sortDirection,
        pageSize,
        getListPaymentsBatchesInitialData,
    ]);

    // Reset de página quando filtros mudam
    useEffect(() => setCurrentPage(1), [ sortField ]);

    useEffect(() => {
        if ((isNewPaymentDrawerOpen === false)) {
            fetchPaymentBatches();
        }
    }, [
      currentPage, 
      sortField, 
      sortDirection, 
      pageSize,
      isNewPaymentDrawerOpen,
    ]);

    useEffect(() => {
        const totalItems = batchesPaginationInfo?.totalElements || 0;
        const newTotalPages = totalItems > 0 ? Math.max(1, Math.ceil(totalItems / pageSize)) : 1;
        
        if (currentPage > newTotalPages && newTotalPages > 0) {
            setCurrentPage(newTotalPages);
        }
    }, [pageSize, batchesPaginationInfo?.totalElements]);

    return (
        <>
          <DrawerDetailsPaymentBatch
            isOpen={isDetailsPaymentBatchDrawerOpen}
            onClose={onDetailsPaymentBatchDrawerClose}
          />
          <DynamicTable
            data={paymentBatches}
            columns={columns}
            loading={isPaymentBatchesLoadingFromHook}
            locale={{ emptyText: "Sem dados de lotes de pagamentos." }}
            pagination={{  currentPage: currentPage,
              pageSize: pageSize,
              onPageSizeChange: (size) => setPageSize(size),
              totalItems: batchesPaginationInfo.totalElements,
              totalPages: batchesPaginationInfo.totalPages,
              onPageChange: handlePageChange
            }}
          />
      
        </>
    );
}
